import React from "react";
import {
  Stack,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import {
  CustomInputIconProps,
  CustomInputProps,
  CustomSelectProps,
} from "../../interface/input";
import { styled } from "@mui/material/styles";
import { labelSty, styInput } from "./styles-input";
import { useTheme } from "@mui/material/styles";
import { StyledTextField } from "./inputs";

// Styled components
export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  // Add any custom styles for FormControl here
}));

export const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: "#f5f7f9",
  borderRadius: "8px",
  "& .MuiSelect-select": {
    backgroundColor: "#f5f7f9",
    color: "#000",
    fontSize: "12px",
    borderRadius: "8px",
  },
}));

export const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
}));

export const CustomInput = ({
  name,
  errors,
  register,
  placeholder,
  labelText,
  type = "text",
}: CustomInputProps) => {
  // Handler to block non-numeric input for number type
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      // Allow: backspace, delete, tab, escape, enter
      if (
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "Tab" ||
        e.key === "Escape" ||
        e.key === "Enter" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown"
      ) {
        return;
      }

      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      // Block if not a number
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    }
  };

  // Handler to block paste of non-numeric content
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      const pastedData = e.clipboardData.getData("text");
      if (!/^\d+$/.test(pastedData)) {
        e.preventDefault();
      }
    }
  };

  // Get input props based on type
  const getInputProps = () => {
    const baseProps = {
      inputProps: {
        style: styInput,
      },
      onKeyDown: handleKeyDown,
      onPaste: handlePaste,
    };

    switch (type) {
      case "number":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "numeric" as const,
            pattern: "[0-9]*",
          },
        };
      case "email":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "email" as const,
          },
        };
      case "tel":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "tel" as const,
          },
        };
      case "url":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "url" as const,
          },
        };
      default:
        return baseProps;
    }
  };

  return (
    <Stack sx={{ marginBottom: 3 }}>
      <InputLabel sx={labelSty}>{labelText}</InputLabel>
      <StyledTextField
        placeholder={placeholder}
        type={type}
        error={!!errors[name]}
        helperText={errors[name] ? errors[name].message : ""}
        {...register(name, {
          valueAsNumber: type === "number",
          valueAsDate: type === "date" || type === "datetime-local",
        } as any)}
        InputProps={getInputProps()}
      />
    </Stack>
  );
};

export const CustomInputIcon = ({
  name,
  errors,
  register,
  placeholder,
  labelText,
  icon,
  type = "text",
}: CustomInputIconProps) => {
  // Handler to block non-numeric input for number type
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      if (
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "Tab" ||
        e.key === "Escape" ||
        e.key === "Enter" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown"
      ) {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        return;
      }

      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      const pastedData = e.clipboardData.getData("text");
      if (!/^\d+$/.test(pastedData)) {
        e.preventDefault();
      }
    }
  };

  const getInputProps = () => {
    const baseProps = {
      endAdornment: <InputAdornment position="end">{icon}</InputAdornment>,
      inputProps: {
        style: styInput,
      },
      onKeyDown: handleKeyDown,
      onPaste: handlePaste,
    };

    switch (type) {
      case "number":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "numeric" as const,
            pattern: "[0-9]*",
          },
        };
      case "email":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "email" as const,
          },
        };
      case "tel":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "tel" as const,
          },
        };
      case "url":
        return {
          ...baseProps,
          inputProps: {
            ...baseProps.inputProps,
            inputMode: "url" as const,
          },
        };
      default:
        return baseProps;
    }
  };

  return (
    <Stack sx={{ marginBottom: 3 }}>
      <InputLabel sx={labelSty}>{labelText}</InputLabel>
      <StyledTextField
        placeholder={placeholder}
        type={type}
        error={!!errors[name]}
        helperText={errors[name] ? errors[name].message : ""}
        {...register(name, {
          valueAsNumber: type === "number",
          valueAsDate: type === "date" || type === "datetime-local",
        } as any)}
        InputProps={getInputProps()}
      />
    </Stack>
  );
};

export const CustomSelect = ({
  name,
  errors,
  register,
  placeholder,
  labelText,
  type = "text",
  ValidTypes,
}: CustomSelectProps) => {
  const theme = useTheme();

  return (
    <Stack sx={{ marginBottom: 3 }}>
      <InputLabel sx={labelSty}>{labelText}</InputLabel>
      <StyledFormControl fullWidth error={!!errors[name]}>
        <StyledSelect
          id="type"
          {...register(name)}
          label="Type"
          defaultValue=""
        >
          <StyledMenuItem
            sx={{ backgroundColor: theme.palette.common.white }}
            value=""
          >
            <em>{placeholder}</em>
          </StyledMenuItem>
          {Object.values(ValidTypes).map((option) => (
            <StyledMenuItem
              key={option}
              value={option}
              sx={{ backgroundColor: theme.palette.common.white }}
            >
              {option}
            </StyledMenuItem>
          ))}
        </StyledSelect>
        {errors[name] && (
          <FormHelperText>
            <>
              {typeof errors[name] === "string"
                ? errors[name]
                : errors[name].message}
            </>
          </FormHelperText>
        )}
      </StyledFormControl>
    </Stack>
  );
};
